
import * as React from 'react';
import { IItemCache, IItemCacheSelector } from '../../models/IItemCacheObserver';
import { IComponentDecorator, IDecoratedComponentType } from '../../utilities/components/Decorators';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { assign } from '@ms/utilities-objects/lib/index';
import { Facet, IFaceted } from '../../models/Facet';
import { IItemCacheStore } from '../../store/createStore';
import { connectWithReduxItemCache, ItemCacheReduxProvider } from './connectToItemCache.v2';

/**
 * @internal
 */
export interface IConnectToItemCacheProviderProps {
  store: IItemCacheStore;
}

/**
 * @internal
 */
export const ConnectToItemCacheProvider: React.ComponentType<IConnectToItemCacheProviderProps> = ItemCacheReduxProvider;

/**
 * Facet which marks a component as having been produced using `connectToItemCache`.
 * The original mapper function and underlying component may be extracted and evaluated
 * directly if necessary, to optimize performance.
 * Care should be taken to pass the underlying component's props to the mapper.
 * @internal
 */
export const connectToItemCacheLinkFacet = new Facet<{
  mapItemCacheToProps: IItemCacheSelector<{}, {}>;
  component: React.ComponentType<{}>;
}>('connectToItemCacheLink');

/**
 * Props interface for a component which optionally consumes the dispatcher for the item cache store.
 * @public
 */
export interface IItemCacheDispatchProp {
  itemCacheDispatch: IItemCacheDispatch;
}

/**
 * Interface for the decorator produced by connectToItemCache, which accounts for the dispatch prop mapping.
 * @public
 */
export interface IConnectToItemCacheDecorator<TInjectedProps, TOwnProps> extends IComponentDecorator<TInjectedProps, TOwnProps> {
  <P extends (TInjectedProps & IItemCacheDispatchProp)>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps & IItemCacheDispatchProp, TOwnProps>;
}

/**
 * Creates a higher-order component which has some props injected based on mappings
 * from the current state of the item cache.
 * @param mapItemCacheToProps - A function which maps the current state of the item cache
 * to props for the component.
 * @public
 */
export function connectToItemCache<TInjectedProps, TOwnProps = {}>(
  mapItemCacheToProps?: IItemCacheSelector<TInjectedProps, TOwnProps>
): IConnectToItemCacheDecorator<TInjectedProps, TOwnProps>;
/**
 * Creates a higher-order component which has some props injected based on mappings
 * from the current state of the item cache.
 * @param mapItemCacheToProps - A function which maps the current state of the item cache
 * to props for the component.
 * @param mapItemCacheDispatchToProps - A function which maps the current store dispatcher for the item cache
 * to props for the component.
 * @public
 */
export function connectToItemCache<TInjectedProps, TDispatchProps = {}, TOwnProps = {}>(
  mapItemCacheToProps: IItemCacheSelector<TInjectedProps, TOwnProps> | undefined,
  mapItemCacheDispatchToProps: (dispatch: IItemCacheDispatch, props: TOwnProps) => TDispatchProps,
): IComponentDecorator<TInjectedProps & TDispatchProps, TOwnProps>;
/**
 * Implementation of `connectToItemCache`.
 * @param mapItemCacheToProps - A function which maps the current state of the item cache
 * to props for the component.
 * @param mapItemCacheDispatchToProps - A function which maps the current store dispatcher for the item cache
 * to props for the component.
 * @internal
 */
export function connectToItemCache<TInjectedProps, TOwnProps = {}>(
  mapItemCacheToProps?: IItemCacheSelector<TInjectedProps, TOwnProps>,
  mapItemCacheDispatchToProps?: (dispatch: IItemCacheDispatch, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps, TOwnProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps, TOwnProps> => {
    const selector = mergeSelectors(mapItemCacheToProps, mapItemCacheDispatchToProps);

    const connectToItemCacheLink = connectToItemCacheLinkFacet.unpack(component as {} as IFaceted);

    const finalSelector = connectToItemCacheLink ?
      componentMapItemCacheToProps(
        selector,
        connectToItemCacheLink.mapItemCacheToProps as {} as IItemCacheSelector<TInjectedProps, TOwnProps>
      ) :
      selector;

    const finalComponent = connectToItemCacheLink ?
      connectToItemCacheLink.component as {} as React.ComponentType<P> :
      component;

    const ConnectToItemCache = connectWithReduxItemCache(finalSelector)(finalComponent);

    assign(ConnectToItemCache, connectToItemCacheLinkFacet.pack({
      mapItemCacheToProps: finalSelector as {} as IItemCacheSelector<{}, {}>,
      component: finalComponent as {} as React.ComponentType<{}>
    }));

    return ConnectToItemCache;
  };
}

function mergeSelectors<TInjectedProps, TOwnProps>(
  mapItemCacheToProps?: IItemCacheSelector<TInjectedProps, TOwnProps>,
  mapItemCacheDispatchToProps?: (dispatch: IItemCacheDispatch, props: TOwnProps) => TInjectedProps
): IItemCacheSelector<TInjectedProps, TOwnProps> {
  function selector(this: void, itemCache: IItemCache, props: TOwnProps): TInjectedProps {
    const { dispatch } = itemCache;

    const itemCacheResult = mapItemCacheToProps ? mapItemCacheToProps(itemCache, props) : {};
    const dispatchResult = mapItemCacheDispatchToProps ? mapItemCacheDispatchToProps(dispatch, props) : {
      itemCacheDispatch: dispatch
    };

    return {
      ...itemCacheResult,
      ...(dispatchResult as {})
    } as TInjectedProps;
  }

  assign(selector, {
    mapItemCacheToProps,
    mapItemCacheDispatchToProps
  });

  return selector;
}

function componentMapItemCacheToProps<TInjectedProps, TProps>(
  outer: IItemCacheSelector<TInjectedProps, TProps>,
  inner: IItemCacheSelector<TInjectedProps, TProps>
): IItemCacheSelector<TInjectedProps, TProps> {
  function composedMapItemCacheToProps(itemCache: IItemCache, props: TProps): TInjectedProps {
    const { dispatch } = itemCache;

    const outerResult = outer(itemCache, props);

    const innerResult = inner(itemCache, {
      ...(props as {}),
      // Need to include the default dispatch mapping when evaluating the wrapped
      // mapper.
      itemCacheDispatch: dispatch,
      ...(outerResult as {})
    } as {} as TProps) as {} as typeof outerResult;

    return {
      // Need to include the outer component's injected props, since they are expected
      // to be available to the wrapped component even if ignored by the inner
      // decorator.
      ...(outerResult as {}),
      ...(innerResult as {})
    } as typeof outerResult;
  }

  assign(composedMapItemCacheToProps, {
    outer,
    inner
  });

  return composedMapItemCacheToProps;
}
